'use client';

import { useEffect, useRef, useState } from 'react';
import type { Chart as ChartType, Plugin } from 'chart.js';
import { barColor, lblColor, solidColor, TOOLTIP_STYLE } from './chartTheme';
import ChartHeader from './ChartHeader';
import Logo from '@/components/icons/Logo';

type ListeningRow = {
  name: string;
  a2: boolean;
  p10: number;
  q1: number;
  med: number;
  q3: number;
  p90: number;
  avg: number;
  std: number;
  ciLo: number;
  ciHi: number;
  optional?: 'anchor' | 'nano';
};

const listeningData: ListeningRow[] = [
  { name: 'Anchor',          a2: false, p10: 0,     q1: 0,  med: 5,    q3: 21,  p90: 50,  avg: 15.60, std: 23.77, ciLo: 14.77, ciHi: 16.42, optional: 'anchor' },
  { name: 'NAM A1-Nano',     a2: false, p10: 13,    q1: 32, med: 74,   q3: 97,  p90: 100, avg: 64.02, std: 33.67, ciLo: 62.84, ciHi: 65.24, optional: 'nano' },
  { name: 'Line 6 Proxy',    a2: false, p10: 19.60, q1: 41, med: 77,   q3: 96,  p90: 100, avg: 67.96, std: 31.24, ciLo: 65.55, ciHi: 70.22 },
  { name: 'TONEX',           a2: false, p10: 24,    q1: 66, med: 90.5, q3: 100, p90: 100, avg: 76.90, std: 30.16, ciLo: 75.83, ciHi: 77.92 },
  { name: 'Neural DSP V2',   a2: false, p10: 54,    q1: 80, med: 94,   q3: 100, p90: 100, avg: 85.38, std: 21.04, ciLo: 84.52, ciHi: 86.13 },
  { name: 'NAM A2-Lite',     a2: true,  p10: 63,    q1: 85, med: 96,   q3: 100, p90: 100, avg: 88.15, std: 18.66, ciLo: 87.50, ciHi: 88.77 },
  { name: 'NAM A1-Standard', a2: false, p10: 64,    q1: 87, med: 97,   q3: 100, p90: 100, avg: 88.80, std: 18.94, ciLo: 88.16, ciHi: 89.44 },
  { name: 'NAM A2-Full',     a2: true,  p10: 77.80, q1: 90, med: 100,  q3: 100, p90: 100, avg: 92.44, std: 14.64, ciLo: 91.93, ciHi: 92.99 },
  { name: 'Recorded',        a2: false, p10: 92,    q1: 96, med: 100,  q3: 100, p90: 100, avg: 97.55, std: 6.14,  ciLo: 97.35, ciHi: 97.77 },
];

export default function A2Mushra() {
  const listeningBoxRef = useRef<HTMLCanvasElement | null>(null);
  const listeningAvgRef = useRef<HTMLCanvasElement | null>(null);

  const [metric, setMetric] = useState<'median' | 'average'>('median');
  const [includeAnchor, setIncludeAnchor] = useState(false);
  const [includeNano, setIncludeNano] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let chart: ChartType | undefined;

    (async () => {
      const { Chart, registerables } = await import('chart.js');
      if (cancelled) return;
      Chart.register(...registerables);

      const filtered = listeningData.filter(
        (d) =>
          (d.optional !== 'anchor' || includeAnchor) &&
          (d.optional !== 'nano' || includeNano),
      );

      if (metric === 'median' && listeningBoxRef.current) {
        const xmin = includeAnchor ? 0 : includeNano ? 30 : 40;

        const boxPlugin: Plugin<'bar'> = {
          id: 'listeningBoxOverlay',
          afterDatasetsDraw(c) {
            const ctx = c.ctx;
            const xAxis = c.scales.x;
            const meta = c.getDatasetMeta(0);
            const capH = 5;
            ctx.save();
            filtered.forEach((d, i) => {
              const bar = meta.data[i];
              if (!bar) return;
              const { base, x: xRight, y: cy, height } =
                bar.getProps(['base', 'x', 'y', 'height'], true) as {
                  base: number;
                  x: number;
                  y: number;
                  height: number;
                };
              const xQ1 = base;
              const xQ3 = xRight;
              const xMed = xAxis.getPixelForValue(d.med);
              const xP10 = xAxis.getPixelForValue(Math.max(d.p10, xmin));
              const xP90 = xAxis.getPixelForValue(Math.min(d.p90, 100));
              const clippedLo = d.p10 < xmin;
              const color = solidColor(d);
              const bw = height;

              ctx.strokeStyle = color;
              ctx.lineWidth = 1.5;
              ctx.globalAlpha = d.a2 ? 0.35 : 1;
              if (clippedLo) {
                ctx.setLineDash([4, 3]);
              }
              ctx.beginPath();
              ctx.moveTo(xQ1, cy);
              ctx.lineTo(xP10, cy);
              ctx.stroke();
              ctx.setLineDash([]);
              if (!clippedLo) {
                ctx.beginPath();
                ctx.moveTo(xP10, cy - capH);
                ctx.lineTo(xP10, cy + capH);
                ctx.stroke();
              }

              ctx.beginPath();
              ctx.moveTo(xQ3, cy);
              ctx.lineTo(xP90, cy);
              ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(xP90, cy - capH);
              ctx.lineTo(xP90, cy + capH);
              ctx.stroke();
              ctx.globalAlpha = 1;

              ctx.lineWidth = 2.5;
              ctx.beginPath();
              ctx.moveTo(xMed, cy - bw / 2);
              ctx.lineTo(xMed, cy + bw / 2);
              ctx.stroke();

              ctx.fillStyle = d.a2 ? color : 'rgba(160,160,168,0.9)';
              ctx.beginPath();
              ctx.arc(xMed, cy, 3, 0, Math.PI * 2);
              ctx.fill();

              ctx.font = '13px Arial, sans-serif';
              ctx.fillStyle = lblColor(d);
              ctx.textAlign = 'left';
              ctx.textBaseline = 'middle';
              ctx.fillText(String(d.med), xP90 + 6, cy);
            });
            ctx.restore();
          },
        };

        chart = new Chart(listeningBoxRef.current, {
          type: 'bar',
          data: {
            labels: filtered.map((d) => d.name),
            datasets: [
              {
                data: filtered.map((d) => [Math.max(d.q1, xmin), d.q3]),
                backgroundColor: filtered.map((d) => barColor(d, 0.25)),
                borderColor: filtered.map((d) => solidColor(d)),
                borderWidth: 1.5,
                borderSkipped: false,
                borderRadius: 3,
                barThickness: 20,
              },
            ],
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 800, easing: 'easeOutQuart' },
            interaction: { mode: 'nearest', axis: 'y', intersect: false },
            plugins: {
              legend: { display: false },
              tooltip: {
                ...TOOLTIP_STYLE,
                callbacks: {
                  title: (items) => filtered[items[0].dataIndex].name,
                  label: (ctx) => {
                    const d = filtered[ctx.dataIndex];
                    return [
                      `Median: ${d.med}`,
                      `Q1 – Q3: ${d.q1} – ${d.q3}`,
                      `P10 – P90: ${d.p10} – ${d.p90}`,
                    ];
                  },
                },
              },
            },
            scales: {
              x: {
                type: 'linear',
                min: xmin,
                max: 100,
                grid: { color: 'rgba(128,128,128,0.15)' },
                ticks: {
                  font: { family: 'Arial', size: 13 },
                  color: 'rgba(255,255,255,0.85)',
                },
                title: {
                  display: true,
                  text: 'Listening test score',
                  font: { family: 'Arial', size: 14 },
                  color: 'rgba(255,255,255,0.9)',
                },
              },
              y: {
                type: 'category',
                grid: { display: false },
                ticks: {
                  font: { family: 'Arial', size: 14 },
                  color: 'rgba(255,255,255,0.85)',
                },
              },
            },
            layout: { padding: { right: 45 } },
          },
          plugins: [boxPlugin],
        });
      } else if (metric === 'average' && listeningAvgRef.current) {
        const ymin = includeAnchor ? 0 : includeNano ? 30 : 40;
        const errColors = filtered.map((d) => solidColor(d));
        const lblColors = filtered.map((d) => lblColor(d));

        const errorBarPlugin: Plugin<'bar'> = {
          id: 'listeningAvgErrorBar',
          afterDatasetsDraw(c) {
            const ctx = c.ctx;
            const xAxis = c.scales.x;
            const yAxis = c.scales.y;
            const capW = 6;
            ctx.save();
            ctx.lineWidth = 2;
            filtered.forEach((d, i) => {
              const cx = xAxis.getPixelForValue(i);
              const loVal = Math.max(d.ciLo, ymin);
              const hiVal = Math.min(d.ciHi, 100);
              const yLo = yAxis.getPixelForValue(loVal);
              const yHi = yAxis.getPixelForValue(hiVal);
              const yMid = yAxis.getPixelForValue(d.avg);
              const clippedHi = d.ciHi > 100;
              const clippedLo = d.ciLo < ymin;
              ctx.strokeStyle = errColors[i];
              ctx.globalAlpha = d.a2 ? 0.35 : 1;
              ctx.beginPath();
              ctx.moveTo(cx, yLo);
              ctx.lineTo(cx, yMid);
              ctx.stroke();
              if (clippedHi) {
                ctx.setLineDash([4, 3]);
              }
              ctx.beginPath();
              ctx.moveTo(cx, yMid);
              ctx.lineTo(cx, yHi);
              ctx.stroke();
              ctx.setLineDash([]);
              if (!clippedHi) {
                ctx.beginPath();
                ctx.moveTo(cx - capW, yHi);
                ctx.lineTo(cx + capW, yHi);
                ctx.stroke();
              }
              if (!clippedLo) {
                ctx.beginPath();
                ctx.moveTo(cx - capW, yLo);
                ctx.lineTo(cx + capW, yLo);
                ctx.stroke();
              }
              ctx.globalAlpha = 1;
              ctx.fillStyle = d.a2 ? errColors[i] : 'rgba(160,160,168,0.9)';
              ctx.beginPath();
              ctx.arc(cx, yMid, 3, 0, Math.PI * 2);
              ctx.fill();
              ctx.font = '13px Arial, sans-serif';
              ctx.fillStyle = lblColors[i];
              ctx.textAlign = 'center';
              ctx.textBaseline = 'bottom';
              ctx.fillText(d.avg.toFixed(1), cx, yHi - 6);
            });
            ctx.restore();
          },
        };

        chart = new Chart(listeningAvgRef.current, {
          type: 'bar',
          data: {
            labels: filtered.map((d) => d.name),
            datasets: [
              {
                data: filtered.map((d) => d.avg),
                backgroundColor: filtered.map((d) => barColor(d, 0.25)),
                borderColor: filtered.map((d) => solidColor(d)),
                borderWidth: 1,
                borderRadius: 3,
                barThickness: 36,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 800, easing: 'easeOutQuart' },
            interaction: { mode: 'nearest', axis: 'x', intersect: false },
            plugins: {
              legend: { display: false },
              tooltip: {
                ...TOOLTIP_STYLE,
                callbacks: {
                  title: (items) => filtered[items[0].dataIndex].name,
                  label: (ctx) => {
                    const d = filtered[ctx.dataIndex];
                    return [
                      `Mean: ${d.avg.toFixed(2)}`,
                      `95% CI: ${d.ciLo.toFixed(2)} – ${d.ciHi.toFixed(2)}`,
                      `Std: ${d.std.toFixed(2)}`,
                    ];
                  },
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: {
                  font: { family: 'Arial', size: 14 },
                  color: 'rgba(255,255,255,0.85)',
                  maxRotation: 30,
                  minRotation: 30,
                  autoSkip: false,
                },
              },
              y: {
                min: ymin,
                max: 100,
                grid: { color: 'rgba(128,128,128,0.15)' },
                ticks: {
                  font: { family: 'Arial', size: 13 },
                  color: 'rgba(255,255,255,0.85)',
                },
                title: {
                  display: true,
                  text: 'Listening test score',
                  font: { family: 'Arial', size: 14 },
                  color: 'rgba(255,255,255,0.9)',
                },
              },
            },
            layout: { padding: { top: 24 } },
          },
          plugins: [errorBarPlugin],
        });
      }
    })();

    return () => {
      cancelled = true;
      chart?.destroy();
    };
  }, [metric, includeAnchor, includeNano]);

  return (
    <div className="w-full border-y border-[#373739] py-4 sm:rounded-2xl sm:border sm:p-8">
      <ChartHeader
        title="Amp Modeler Blind Listening Test"
        description="Over 1,000 participants compared recordings of real gear to anonymized digital models using the MUSHRA blind listening methodology. Higher scores mean the model was rated as sounding closer to the original amp, pedal, or signal chain."
      />
      <div className="w-full rounded-2xl border border-[#373739] p-4 sm:p-6">
        <div className="text-lg font-bold text-white">MUSHRA Listening Test Scores</div>
        <div className="mb-3 mt-1.5 text-base text-zinc-400">
          {metric === 'median'
            ? 'Median, Q1/Q3, and P10/P90 whiskers, higher is better'
            : 'Mean with 95% bootstrap CI, higher is better'}
        </div>
        {metric === 'median' ? (
          <div className="relative h-[340px] w-full max-[500px]:h-[280px]">
            <canvas ref={listeningBoxRef} />
          </div>
        ) : (
          <div className="relative h-[440px] w-full max-[500px]:h-[380px]">
            <canvas ref={listeningAvgRef} />
          </div>
        )}
        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-[#373739] pt-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-zinc-500">View</span>
            <div className="flex items-center gap-1 rounded-lg bg-white/5 p-0.5">
              <button
                type="button"
                onClick={() => setMetric('median')}
                className={`rounded-md px-2.5 py-1 transition ${
                  metric === 'median'
                    ? 'bg-white/10 text-white'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Median (Q1/Q3)
              </button>
              <button
                type="button"
                onClick={() => setMetric('average')}
                className={`rounded-md px-2.5 py-1 transition ${
                  metric === 'average'
                    ? 'bg-white/10 text-white'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Mean (95% CI)
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-zinc-500">Include</span>
            <label className="flex cursor-pointer items-center gap-1.5 text-zinc-300">
              <input
                type="checkbox"
                checked={includeAnchor}
                onChange={(e) => setIncludeAnchor(e.target.checked)}
                className="accent-[#FFFF00]"
              />
              Anchor
            </label>
            <label className="flex cursor-pointer items-center gap-1.5 text-zinc-300">
              <input
                type="checkbox"
                checked={includeNano}
                onChange={(e) => setIncludeNano(e.target.checked)}
                className="accent-[#FFFF00]"
              />
              NAM A1-Nano
            </label>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Logo style={{ height: 20, width: 'auto' }} />
      </div>
    </div>
  );
}
